import { DefaultError, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { vcardMicrositeAPIService } from '~/api/microsites/vcard-plus';
import { websiteAPIService } from '~/api/microsites/website';
import { qrcodeAPIService } from '~/api/qrcode';
import { qrCodeKeys } from '~/constants/queryKeys';

interface UseQrCodeByIdParams<TData> {
  qrCodeId?: string | number;
  select?: (data: AxiosResponse<any>) => TData;
}

export const useQrCodeById = <TData>({
  qrCodeId,
  select,
}: UseQrCodeByIdParams<TData>): UseQueryResult<TData, DefaultError> => {
  return useQuery({
    queryKey: qrCodeKeys.qrCodeById(qrCodeId),
    queryFn: () => qrcodeAPIService.getQrCodeById(qrCodeId),
    enabled: !!qrCodeId,
    refetchOnWindowFocus: false,
    select,
  });
};

interface UseWebsiteQrByIdParams<TData = unknown> {
  websiteQrId?: string;
  select?: (data: AxiosResponse<any>) => TData;
  enabled?: boolean;
}

export const useWebsiteQrById = <TData = AxiosResponse<any>>({
  websiteQrId,
  select,
  enabled = true,
}: UseWebsiteQrByIdParams<TData>): UseQueryResult<TData, DefaultError> => {
  return useQuery({
    queryKey: qrCodeKeys.websiteQRDataById(websiteQrId),
    queryFn: () => websiteAPIService.getWebsiteSite({ id: websiteQrId }),
    enabled: !!websiteQrId && enabled,
    refetchOnWindowFocus: false,
    select,
  });
};

interface UseVCardQrByIdParams {
  vCardId?: string | number;
  enabled?: boolean;
}

export const useVCardQrById = ({ vCardId, enabled = true }: UseVCardQrByIdParams) => {
  return useQuery({
    queryKey: qrCodeKeys.vCardPlusQRDataById(vCardId),
    queryFn: () => vcardMicrositeAPIService.getVCardSite(vCardId),
    enabled: !!vCardId && enabled,
    select: (res) => res.data,
  });
};
